import { useQuery } from '@tanstack/react-query';

import type { LoginResponse } from '@/api/types/auth';
import { queryKeys } from '@/config/query/queryKeys';

interface UserInfo {
  email: string | null;
  hasSeenNarrativeIntro: boolean | undefined;
  initial: string;
  name: string | null;
}

/** "jainil.parekh@lucioai.com" → "Jainil Parekh" */
function nameFromEmail(email: string): string | null {
  return (
    email
      .split('@')[0]
      ?.split('.')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ') ?? null
  );
}

function initialFrom(name: string | null, email: string | null): string {
  const trimmedName = name?.trim() ?? '';
  if (trimmedName) {
    const firstChar = trimmedName.split(/\s+/)[0]?.charAt(0);
    if (firstChar) {
      return firstChar.toUpperCase();
    }
  }
  const firstChar = email?.trim().charAt(0);
  return firstChar ? firstChar.toUpperCase() : 'U';
}

/**
 * The signed-in user's display identity, derived from the server profile
 * (`/auth/me`, cached under queryKeys.auth.user()) — the source of truth
 * since Clerk became the primary identity. The old MSAL-account source is
 * gone: the MSAL cache is only populated around the Connect Mailbox popup
 * and is empty on a fresh taskpane load.
 */
export function useUserInfo(): UserInfo {
  const { data: userData } = useQuery<LoginResponse['user']>({
    enabled: false, // Don't fetch, just read from cache
    queryKey: queryKeys.auth.user(),
  });

  const email = userData?.email ?? null;
  const name = userData?.displayName || (email ? nameFromEmail(email) : null);

  return {
    email,
    hasSeenNarrativeIntro: userData?.hasSeenNarrativeIntro,
    initial: initialFrom(name, email),
    name,
  };
}
